import {
	CheckCircleOutlined,
	CloudSyncOutlined,
	CloudUploadOutlined,
} from "@ant-design/icons";
import { Button, Card, Empty, Spin, Table, Tag, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { getAllAperturasDeCaja } from "../../services/aperturaDeCajaService";
import { getAllCajas } from "../../services/cajaService";
import { getSyncConfig, syncAll } from "../../services/syncService";
import { getAllUsers } from "../../services/userService";

const AperturaDeCajaList = ({ refreshTrigger, onEditApertura }) => {
	const [aperturas, setAperturas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [syncingAll, setSyncingAll] = useState(false);
	const isMobile = window.innerWidth < 768;
	const [config, setConfig] = useState({
		interval: 30,
		intervalUnit: "segundos",
	});
	const [users, setUsers] = useState([]);
	const [cajas, setCajas] = useState([]);

	// Cargar la configuración y datos de referencias al inicio
	useEffect(() => {
		const fetchInitialData = async () => {
			try {
				// Cargar configuración
				const syncConfig = await getSyncConfig();
				setConfig(syncConfig);

				// Cargar datos de usuarios para mostrar nombres en lugar de códigos
				const usersData = await getAllUsers();
				setUsers(usersData);

				// Cargar datos de cajas para mostrar nombres en lugar de códigos
				const cajasData = await getAllCajas();
				setCajas(cajasData);
			} catch (error) {
				console.error("Error al cargar datos iniciales:", error);
			}
		};

		fetchInitialData();
	}, []);

	// Cargar aperturas al inicio y cuando cambie el trigger
	useEffect(() => {
		loadAperturas();
	}, [refreshTrigger]);

	// Actualizar la lista según el intervalo configurado
	useEffect(() => {
		const interval = setInterval(() => {
			loadAperturas();
		}, config.interval * (config.intervalUnit === "segundos" ? 1000 : config.intervalUnit === "minutos" ? 60000 : 3600000));

		// Limpiar intervalo al desmontar
		return () => clearInterval(interval);
	}, [config]);

	const loadAperturas = async () => {
		try {
			console.log("Cargando aperturas de caja...");
			setLoading(true);
			const aperturasList = await getAllAperturasDeCaja();
			console.log("Aperturas cargadas:", aperturasList);
			setAperturas(aperturasList);
		} catch (error) {
			window.messageApi.error(
				"Error al cargar aperturas de caja: " + error.message
			);
		} finally {
			setLoading(false);
		}
	};

	// Renderizar el estado de sincronización
	const renderSyncStatus = (pendingSync, lastSynced) => {
		if (pendingSync === 1) {
			return (
				<Tooltip title="Pendiente de sincronizar">
					<Tag icon={<CloudUploadOutlined />} color="warning">
						Pendiente
					</Tag>
				</Tooltip>
			);
		} else {
			const syncDate = lastSynced
				? new Date(lastSynced).toLocaleString()
				: "Nunca";

			return (
				<Tooltip title={`Última sincronización: ${syncDate}`}>
					<Tag icon={<CheckCircleOutlined />} color="success">
						Sincronizado
					</Tag>
				</Tooltip>
			);
		}
	};

	// Sincronizar todas las aperturas
	const handleSyncAll = async () => {
		try {
			setSyncingAll(true);
			await syncAll();
			// Recargar la lista para reflejar los cambios
			await loadAperturas();
			window.messageApi.success("Sincronización completada");
		} catch (error) {
			window.messageApi.error(
				"Error durante la sincronización: " + error.message
			);
		} finally {
			setSyncingAll(false);
		}
	};

	// Obtener el nombre del cajero a partir del código
	const getCajeroName = (cajeroNo) => {
		const cajero = users.find((user) => user.No === cajeroNo);
		return cajero ? `${cajero.firstName} ${cajero.lastName}` : cajeroNo;
	};

	// Obtener el nombre de la caja a partir del código
	const getCajaName = (cajaNo) => {
		const caja = cajas.find((caja) => caja.No === cajaNo);
		return caja ? caja.nombre : cajaNo || "No asignada";
	};

	// Renderizar el estado con color
	const renderEstado = (estado) => {
		let color;
		switch (estado) {
			case "Abierto":
				color = "green";
				break;
			case "Cerrado":
				color = "red";
				break;
			case "Turno Generado":
				color = "blue";
				break;
			default:
				color = "default";
		}
		return <Tag color={color}>{estado}</Tag>;
	};

	const columns = [
		{
			title: "ID",
			dataIndex: "id",
			key: "id",
		},
		{
			title: "No.",
			dataIndex: "No",
			key: "No",
			render: (No) => No || "Pendiente",
		},
		{
			title: "Cajero",
			dataIndex: "Cajero",
			key: "Cajero",
			render: (cajeroNo) => getCajeroName(cajeroNo),
		},
		{
			title: "Caja",
			dataIndex: "Caja",
			key: "Caja",
			render: (cajaNo) => getCajaName(cajaNo),
		},
		{
			title: "Fecha",
			dataIndex: "FechaDeApertura",
			key: "FechaDeApertura",
			render: (text) => new Date(text).toISOString().split("T")[0],
		},
		{
			title: "Hora",
			dataIndex: "HoraDeApertura",
			key: "HoraDeApertura",
			render: (text) => text || "00:00",
		},
		{
			title: "Importe",
			dataIndex: "ImporteDeApertura",
			key: "ImporteDeApertura",
			render: (value) => `${value.toFixed(2)} $`,
		},
		{
			title: "Estado",
			dataIndex: "Estado",
			key: "Estado",
			render: renderEstado,
		},
		{
			title: "Turno",
			dataIndex: "Turno",
			key: "Turno",
		},
		{
			title: "Sincronización",
			key: "syncStatus",
			render: (_, record) =>
				renderSyncStatus(record.pendingSync, record.lastSynced),
		},
	];

	if (loading) {
		return <Spin size="large" />;
	}

	if (aperturas.length === 0) {
		return <Empty description="No hay aperturas de caja registradas aún" />;
	}

	return (
		<div className="apertura-caja-list-container">
			<Card
				title={isMobile ? "Aperturas de Caja" : "Aperturas de Caja Registradas"}
				extra={
					<Button
						icon={<CloudSyncOutlined />}
						onClick={handleSyncAll}
						loading={syncingAll}
						type="primary"
					>
						{isMobile ? "" : "Sincronizar Todos"}
					</Button>
				}
			>
				<Table
					columns={columns}
					dataSource={aperturas.map((apertura) => ({
						...apertura,
						key: apertura.id,
						No: apertura.No !== null ? apertura.No.toString() : "Pendiente",
					}))}
					pagination={{ pageSize: 5 }}
					bordered
					size="middle"
					scroll={{ x: "max-content" }}
					rowKey="id"
				/>
			</Card>
		</div>
	);
};

export default AperturaDeCajaList;
